import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/axios';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, ShieldCheck, Wrench } from 'lucide-react';
import { TariffAssignDialog } from '@/pages/Dashboard/TariffAssignDialog';
import { DataQualityStudentWizard } from '@/pages/Dashboard/DataQualityStudentWizard';

const CATEGORIES = [
  { key: 'missing_payer', label: 'Ohne Zahler', hint: 'Kein zahlender Angehöriger und keine Zahler-Zuordnung — SEPA-Lauf würde diese Schüler überspringen.' },
  { key: 'missing_family', label: 'Ohne Familie', hint: 'Weder Familien- noch Angehörigen-Verknüpfung vorhanden.' },
  { key: 'missing_tariff', label: 'Ohne Tarif', hint: 'Aktive Belegung in einem Kurs ohne Tarif — Abrechnung würde 0 € ergeben.' },
  { key: 'missing_contract', label: 'Ohne Vertrag', hint: 'Aktive Belegung, aber kein aktiver Vertrag hinterlegt.' },
];

const WIZARD_KEYS = new Set(['missing_payer', 'missing_family', 'missing_contract']);

export default function DataQualityWidget() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [wizard, setWizard] = useState(null); // category key

  const loadData = () => {
    api.get('/reports/data-quality')
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  };

  useEffect(loadData, []);

  if (error) return null;

  const visibleCategories = CATEGORIES.filter(
    (c) => !(c.key === 'missing_contract' && data && !data.contracts_used)
  );

  const totalIssues = data
    ? visibleCategories.reduce((sum, c) => sum + (data.counts[c.key] || 0), 0)
    : 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-8" data-testid="data-quality-widget">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-5 h-5 text-brand-red" />
        <h2 className="text-xl font-semibold text-slate-900">Datenqualität</h2>
        {data && (
          totalIssues === 0 ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="data-quality-all-ok">
              <CheckCircle2 className="w-3 h-3" /> Alles vollständig
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-red bg-brand-red/5 border border-brand-red/20 rounded-full px-2 py-0.5" data-testid="data-quality-issue-count">
              <AlertTriangle className="w-3 h-3" /> {totalIssues} Auffälligkeit{totalIssues === 1 ? '' : 'en'}
            </span>
          )
        )}
      </div>
      <p className="text-xs text-slate-500 mb-4">
        {data ? `Geprüft: ${data.active_students} aktive Schüler (mit laufender Belegung)` : 'Prüfe aktive Schüler...'}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {visibleCategories.map((cat) => {
          const count = data?.counts?.[cat.key] ?? null;
          const list = data?.[cat.key] || [];
          const isOpen = open === cat.key;
          const ok = count === 0;
          return (
            <div key={cat.key}
                 className={`rounded-lg border ${ok ? 'border-emerald-200 bg-emerald-50/50' : 'border-brand-red/20 bg-brand-red/5'}`}>
              <button
                onClick={() => setOpen(isOpen ? null : cat.key)}
                disabled={ok || count === null}
                className="w-full flex items-center justify-between px-3 py-2.5 text-left"
                data-testid={`data-quality-${cat.key}`}
                title={cat.hint}
              >
                <span className="text-sm font-medium text-slate-800">{cat.label}</span>
                <span className="flex items-center gap-1.5">
                  {count === null ? (
                    <span className="h-5 w-8 bg-slate-200 animate-pulse rounded" />
                  ) : (
                    <span className={`text-sm font-bold ${ok ? 'text-emerald-700' : 'text-brand-red'}`}
                          data-testid={`data-quality-count-${cat.key}`}>
                      {count}
                    </span>
                  )}
                  {!ok && count !== null && (isOpen
                    ? <ChevronUp className="w-4 h-4 text-slate-400" />
                    : <ChevronDown className="w-4 h-4 text-slate-400" />)}
                </span>
              </button>
              {isOpen && (
                <div className="max-h-52 overflow-y-auto border-t border-brand-red/15 px-3 py-2 space-y-1" data-testid={`data-quality-list-${cat.key}`}>
                  <p className="text-[11px] text-slate-500 mb-1">{cat.hint}</p>
                  {cat.key === 'missing_tariff' && (
                    <button onClick={(e) => { e.stopPropagation(); setAssignOpen(true); }}
                            className="w-full flex items-center justify-center gap-1.5 rounded-md bg-brand-red hover:bg-brand-red/90 text-white text-xs font-semibold py-1.5 mb-1"
                            data-testid="open-tariff-assign-btn">
                      <Wrench className="w-3 h-3" /> Tarife jetzt zuordnen
                    </button>
                  )}
                  {WIZARD_KEYS.has(cat.key) && (
                    <button onClick={(e) => { e.stopPropagation(); setWizard(cat.key); }}
                            className="w-full flex items-center justify-center gap-1.5 rounded-md bg-brand-red hover:bg-brand-red/90 text-white text-xs font-semibold py-1.5 mb-1"
                            data-testid={`open-dq-wizard-${cat.key}`}>
                      <Wrench className="w-3 h-3" /> Jetzt zuordnen
                    </button>
                  )}
                  {list.map((s) => (
                    <Link key={s.id} to={`/students/${s.id}`}
                          className="block text-xs text-slate-700 hover:text-brand-red hover:underline truncate"
                          data-testid={`data-quality-student-${s.id}`}>
                      {s.name}{s.kassenzeichen ? ` (${s.kassenzeichen})` : ''}
                    </Link>
                  ))}
                  {(data?.counts?.[cat.key] || 0) > list.length && (
                    <p className="text-[11px] text-slate-400 italic">… und {data.counts[cat.key] - list.length} weitere</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <TariffAssignDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onAssigned={loadData}
        initialStudents={data?.missing_tariff || []}
      />
      {wizard && (
        <DataQualityStudentWizard
          open={Boolean(wizard)}
          category={wizard}
          initialStudents={data?.[wizard] || []}
          onClose={() => { setWizard(null); loadData(); }}
          onFixed={loadData}
        />
      )}
    </div>
  );
}
